import {
  DatumLayer,
  LayerCollection,
  MapProperties,
} from '@datum-group/datum-map/lib';
import { Layer } from '@datum-group/isogd-openapi/lib';
import { Services } from '@project/API';
import {
  action,
  computed,
  makeAutoObservable,
  observable,
  runInAction,
} from 'mobx';
import TileLayer from 'ol/layer/Tile';
import { XYZ } from 'ol/source';

const QUERY = {
  query: {
    layer_type: 'XYZ',
  },
};

export enum loadingStatus {
  loading = 'loading',
  loaded = 'loaded',
  notLoaded = 'notLoaded',
}

export const BACKGROUND_TILE_URL =
  'https://core-renderer-tiles.maps.yandex.net/tiles?l=map&x={x}&y={y}&z={z}&projection=web_mercator&maptype=future_map';

export class BaseLayersStore {
  @observable private _activeBaseLayer?: Layer = undefined;
  @observable private _error?: string = undefined;
  @observable private _status: loadingStatus = loadingStatus.notLoaded;
  private _baseLayers: Layer[] = [];
  private _services: Services;
  private _mapProperties: MapProperties;
  private _layerCollection: LayerCollection;
  private _backgroundLayer: TileLayer;

  constructor(
    services: Services,
    mapProperties: MapProperties,
    layerCollection: LayerCollection,
  ) {
    this._services = services;
    this._mapProperties = mapProperties;
    this._layerCollection = layerCollection;
    this._backgroundLayer = new TileLayer({
      source: new XYZ({
        url: BACKGROUND_TILE_URL,
        crossOrigin: 'anonymous',
      }),
      zIndex: 0,
    });

    makeAutoObservable(this);
  }

  @computed get status() {
    return this._status;
  }

  @computed get error() {
    return this._error;
  }

  @computed getActiveLayer() {
    return this._activeBaseLayer ? { ...this._activeBaseLayer } : undefined;
  }

  @computed get baseLayers() {
    return this._baseLayers.map((layer) => {
      return { ...layer };
    });
  }

  @action async load() {
    runInAction(() => {
      this._error = undefined;
      this._status = loadingStatus.loading;
    });
    try {
      const response = await this._services.map.apiLayersList(1, 0, QUERY);
      runInAction(() => {
        this._baseLayers = response.data.results;
        this._status = loadingStatus.loaded;
      });
    } catch (e) {
      runInAction(
        () => (this._error = 'Не удалось загрузить картографические подложки'),
      );
      runInAction(() => (this._status = loadingStatus.notLoaded));
    }
  }

  private removeLayerFromCollection(layerName: string) {
    this._layerCollection.remove(layerName);
  }

  setActiveLayer(layerName: string) {
    if (!this.isBaseLayer(layerName)) return;
    if (this._activeBaseLayer) {
      this.removeLayerFromCollection(this._activeBaseLayer.layer_name);
      this._mapProperties.map.removeLayer(this._backgroundLayer);
    }
    try {
      const layer = this._baseLayers.find(
        (layer) => layer.layer_name === layerName,
      );
      if (typeof layer === 'undefined') return;
      const baseLayer = this._layerCollection.layers.find(
        (layer) => layer.name === layerName,
      );

      if (baseLayer && this.isBaseLayer(baseLayer.name)) {
        this.removeLayerFromCollection(baseLayer.name);
        this.setActiveLayer(baseLayer.name);
      } else {
        if (layerName === 'Donland_gisro_map') {
          this._mapProperties.map.addLayer(this._backgroundLayer);
        }

        const activeLayer = this._layerCollection.add(layer);

        // ToDo: Реализовать на стороне datum-map / не использовать доступ к приватным полям
        // @ts-expect-error no typings
        activeLayer._olSource.crossOrigin = 'anonymous';

        this.setLayerPosition(activeLayer);
        runInAction(() => (this._activeBaseLayer = layer));
      }
    } catch (e) {
      runInAction(() => (this._error = 'Не удалось добавить слой на карту'));
    }
  }

  get currentLayerNames(): string[] {
    return this._layerCollection.layers.map((layer) => layer.name);
  }

  private setLayerPosition(layer: DatumLayer) {
    const allLayers = this._mapProperties.map.getLayers().getArray();
    const maxIndex = allLayers
      .reduce((acc, curr) => {
        const accZIndex = acc.getZIndex() ?? 0;
        const currZIndex = curr.getZIndex() ?? 0;

        return accZIndex > currZIndex ? acc : curr;
      })
      .getZIndex();

    maxIndex
      ? this._layerCollection.setLayerPosition(layer, maxIndex + 1)
      : this._layerCollection.setLayerPosition(layer, 1);
  }

  isBaseLayer(layerName: string) {
    return this.baseLayers.some((layer) => layer.layer_name === layerName);
  }
}
